import * as React from 'react';
import { DotsLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface TimelineItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: React.ReactNode;
  date: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** When true, hides the connecting line below this item (for the last item). */
  last?: boolean;
}

export const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(
  ({ icon, date, title, description, last, className, children, ...props }, ref) => (
    <div ref={ref} className={cn('relative flex gap-3 pb-5', className)} {...props}>
      <div className="relative flex flex-col items-center">
        <div
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full border',
            'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-fg-secondary)]',
            'shrink-0',
          )}
        >
          {icon ?? <DotsLine size={10} />}
        </div>
        {!last && (
          <span
            aria-hidden
            className="absolute left-1/2 top-7 -translate-x-1/2 w-px grow bg-[var(--color-border)]"
            style={{ height: 'calc(100% - 1.75rem)' }}
          />
        )}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-sm font-medium text-[var(--color-fg)]">{title}</div>
          <div className="text-xs text-[var(--color-fg-muted)]">{date}</div>
        </div>
        {description && (
          <div className="mt-1 text-sm text-[var(--color-fg-secondary)]">{description}</div>
        )}
        {children}
      </div>
    </div>
  ),
);
TimelineItem.displayName = 'TimelineItem';

export const Timeline = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const items = React.Children.toArray(children);
    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...props}>
        {items.map((child, idx) => {
          if (React.isValidElement<TimelineItemProps>(child) && child.type === TimelineItem) {
            return React.cloneElement(child, { last: idx === items.length - 1 });
          }
          return child;
        })}
      </div>
    );
  },
);
Timeline.displayName = 'Timeline';
